import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Leaf, ArrowLeft, Wallet, Plus, DollarSign, TrendingUp,
  Package, Clock, CheckCircle, Loader2, CreditCard, ArrowDownLeft, ArrowUpRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellerWalletPage = () => {
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [addAmount, setAddAmount] = useState("25");
  const [addingFunds, setAddingFunds] = useState(false);

  useEffect(() => {
    if (searchParams.get("funded") === "true") {
      toast.success("Funds added to your wallet!");
      // Confirm the top-up
      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        confirmTopup(sessionId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      loadWalletData();
    }
  }, [user]);

  const confirmTopup = async (sessionId) => {
    try {
      await axios.post(`${API}/seller/wallet/confirm-topup`, { session_id: sessionId }, { withCredentials: true });
      loadWalletData();
    } catch (error) {
      console.error("Failed to confirm topup:", error);
    }
  };

  const loadWalletData = async () => {
    try {
      const [walletRes, earningsRes] = await Promise.all([
        axios.get(`${API}/seller/wallet`, { withCredentials: true }),
        axios.get(`${API}/seller/earnings`, { withCredentials: true })
      ]);
      setWallet(walletRes.data);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error("Failed to load wallet:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFunds = async () => {
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount < 5 || amount > 500) {
      toast.error("Amount must be between $5 and $500");
      return;
    }

    setAddingFunds(true);
    try {
      const response = await axios.post(
        `${API}/seller/wallet/add-funds`,
        { amount },
        { withCredentials: true }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setAddingFunds(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full card-organic">
          <CardContent className="pt-8 text-center">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-primary/50" />
            <h2 className="font-serif text-2xl font-bold mb-2">Seller Wallet</h2>
            <p className="text-muted-foreground mb-6">
              Sign in to manage your seller wallet and earnings.
            </p>
            <Button onClick={login} className="btn-primary">
              Sign In with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/marketplace" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-serif text-xl font-bold text-foreground flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Seller Wallet
            </h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
            
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Balance Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="card-organic bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-700 text-sm font-medium">Available Balance</p>
                  <p className="text-3xl font-bold text-green-800">
                    ${wallet?.balance?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-xs text-green-600 mt-1">Ready for shipping labels</p>
                </div>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-organic bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-700 text-sm font-medium">Pending Earnings</p>
                  <p className="text-3xl font-bold text-amber-800">
                    ${wallet?.pending_balance?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">From orders awaiting shipment</p>
                </div>
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-7 h-7 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-organic bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-700 text-sm font-medium">Total Earned</p>
                  <p className="text-3xl font-bold text-blue-800">
                    ${earnings?.total_payouts?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">{earnings?.orders_count || 0} orders completed</p>
                </div>
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Add Funds Card */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                Add Funds
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Add funds to purchase shipping labels. You can also deduct label costs from your order payouts.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label>Amount (USD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="5"
                      max="500"
                      value={addAmount}
                      onChange={(e) => setAddAmount(e.target.value)}
                      className="pl-8"
                      placeholder="25.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Min $5, Max $500</p>
                </div>

                {/* Quick amount buttons */}
                <div className="flex gap-2">
                  {[10, 25, 50, 100].map((amt) => (
                    <Button
                      key={amt}
                      variant={addAmount === String(amt) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAddAmount(String(amt))}
                      className="flex-1"
                    >
                      ${amt}
                    </Button>
                  ))}
                </div>

                <Button
                  onClick={handleAddFunds}
                  className="w-full btn-primary"
                  disabled={addingFunds}
                  data-testid="add-funds-btn"
                >
                  {addingFunds ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Add ${addAmount || "0"}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card className="card-organic lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Transaction History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-y-auto space-y-3">
                {!wallet?.transactions?.length ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No transactions yet</p>
                    <p className="text-sm">Your wallet activity will appear here</p>
                  </div>
                ) : (
                  wallet.transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.amount > 0 ? "bg-green-100" : "bg-red-100"
                        }`}>
                          {tx.amount > 0 ? (
                            <ArrowDownLeft className="w-5 h-5 text-green-600" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {tx.type === "topup" && "Wallet Top-up"}
                            {tx.type === "label_purchase" && "Label Purchase"}
                            {tx.type === "label_from_payout" && "Label (from payout)"}
                            {tx.type === "payout" && "Payout"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className={`font-bold ${tx.amount > 0 ? "text-green-600" : "text-red-600"}`}>
                        {tx.amount > 0 ? "+" : ""}${Math.abs(tx.amount).toFixed(2)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Earnings Breakdown */}
        {earnings && (
          <Card className="card-organic mt-6">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Earnings Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-xl font-bold">${earnings.total_sales?.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Commission Paid (18%)</p>
                  <p className="text-xl font-bold text-red-600">-${earnings.total_commission_paid?.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Label Costs</p>
                  <p className="text-xl font-bold text-red-600">-${earnings.total_label_costs?.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">Net Earnings</p>
                  <p className="text-xl font-bold text-green-700">${earnings.total_payouts?.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How Labels Work Info */}
        <div className="mt-8 p-6 bg-muted/30 rounded-2xl">
          <h3 className="font-serif font-bold mb-4">How Shipping Labels Work</h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Option 1: Prepay with Wallet
              </h4>
              <p className="text-muted-foreground">
                Add funds to your wallet and purchase labels instantly. Great for high-volume sellers who want quick shipping.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Option 2: Deduct from Payout
              </h4>
              <p className="text-muted-foreground">
                Have the label cost deducted from your order payout. No upfront cost, but your payout will be reduced.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SellerWalletPage;
